'use client';

import React, { useState, useEffect, useRef } from 'react';
import Button from '../ui/button';
import Scroll from '../shared/scroll';
import { cn } from '@/lib/utils';
import { useModalStore } from '@/helpers/modal_store';
import { useSizeModal } from '@/helpers/size_guide_modal';
import { ShoppingCartSimple } from '@phosphor-icons/react';
import { useVariantPrice } from '@/helpers/variant_price';
import { Product, RelatedProducts } from '@/types/product_page/productType';

const SecondaryNavbar = ({
  name,
  data,
  related,
}: {
  name: string;
  code: string;
  data: Product;
  link?: string;
  related?: RelatedProducts[] | null;
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [isFooterCrossed, setIsFooterCrossed] = useState(false);
  const stickyElementRef = useRef<HTMLDivElement>(null);
  const { price } = useVariantPrice();

  const { toggleModal } = useModalStore();
  const { isSizeModalOpen } = useSizeModal();

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;

      if (currentPosition > scrollTop + 3) {
        setScrolling(true);
      } else if (currentPosition < scrollTop - 3) {
        setScrolling(false);
      }
      setScrollTop(currentPosition);

      const footer = document.getElementById('footer');

      if (footer) {
        const footerTop = footer.getBoundingClientRect().top;

        if (footerTop < 40) {
          setIsFooterCrossed(true);
        } else {
          setIsFooterCrossed(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollTop]);

  useEffect(() => {
    const handleScroll = () => {
      const stickyElement = stickyElementRef.current;

      if (stickyElement) {
        const rect = stickyElement.getBoundingClientRect();
        setIsSticky(rect.top <= 150);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.body.classList.add('product-page');

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('product-page');
    };
  }, []);

  const onClick = () => {
    const buy_section = document.getElementById('buy-section');
    const offset = 300;

    if (data.enable_e_commerce && buy_section) {
      buy_section?.scrollIntoView({ behavior: 'smooth' });
      const elementPosition =
        buy_section.getBoundingClientRect().top + window.scrollY;

      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    } else {
      toggleModal(true);
    }
  };

  return (
    <div ref={stickyElementRef} className="w-full relative font-proxima_font">
      <div
        className={cn(
          'md:bg-white  border-b border-[#F2F2F7] flex flex-col md:flex-row fixed md:block px-4 overflow-scroll no-scrollbar -mx-4 md:fixed left-0 right-0  transition-all duration-300 transform',
          scrolling
            ? 'bottom-[-1px] md:bottom-unset md:top-0'
            : 'bottom-[-1px] md:bottom-unset md:top-16',
          scrollTop > 400 ? 'z-20' : '-z-20  pointer-events-none',
          isSticky ? 'opacity-100' : 'opacity-0 pointer-events-none',
          isFooterCrossed ? 'opacity-0 pointer-events-none' : '',
          isSizeModalOpen ? '-z-10' : 'z-20'
        )}
      >
        <div
          className={cn(
            'max-w-desktop h-[56px] mx-auto hidden md:flex items-center justify-between w-full px-6 md:px-4'
          )}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex flex-col flex-1">
              <p className="font-bold text-[16px] md:font-semibold">{name}</p>
            </div>

            <nav className="hidden md:flex justify-center w-full lg:w-auto lg:gap-6 items-center font-bold list-none flex-[2]">
              {!!data?.sections.length && (
                <Scroll to="features" label="Overview" />
              )}
              {!!data?.specs.length && (
                <Scroll to="specifications" label="Specifications" />
              )}

              {!!data?.faq.length && <Scroll to="faqs" label="FAQ" />}
              {!!data?.downloadables.length && (
                <Scroll to="downloads" label="Downloads" />
              )}
              {!!related?.length && (
                <Scroll to="accessories" label="Accessories" />
              )}
              {/* {data?.review_page && <Scroll to="reviews" label="Reviews" />} */}
            </nav>

            <div className="flex gap-4 lg:gap-8 flex-1 justify-end">
              <h3 className="product-price text-[16px] font-semibold py-1 hidden md:block">
                ${price}
              </h3>
              <Button
                label="Where To Buy"
                className="where-to-buy-btn !text-[14px] py-2 md:py-1 hidden md:block"
                variant="fill"
                onClick={() => toggleModal(true)}
              />
              <Button
                label={`Add $${price}`}
                className="lg:text-base py-2 md:py-1 md:hidden"
                variant="black"
                onClick={() => {}}
              />
            </div>
          </div>
        </div>

        {/* For mobile */}
        <button
          onClick={onClick}
          className="where-to-buy-btn bg-black text-white font-semibold w-full p-4 md:hidden flex items-center justify-center gap-2"
        >
          Where to Buy
          {/* <ShoppingCartSimple size={18} /> */}
        </button>
      </div>
    </div>
  );
};

export default SecondaryNavbar;
